/**
 * 右上角**全局归属**（品牌 / 站点）的**唯一状态源**（R12）。
 *
 * ## 为什么是 localStorage 而不是 URL search（任务要求二选一，这里给理由）
 *
 *  1. **它必须同时驱动多个页面**（广告管理 / 货号看板 / 素材看板），而本项目的
 *     `router.tsx` 是**按路由 schema 序列化**的（`stringifySearch` 用 key 集合区分
 *     AdsSearch / AdManagementSearch）。把全局归属塞进每个路由的 schema，等于
 *     给每个页面各存一份，跨页导航时要靠每个 `<Link>` 手工透传——任何一处漏传，
 *     「右上角选的是 US，页面显示的是全部站点」这类错就会静默发生。
 *  2. **刷新后保持**：localStorage 天然满足。
 *  3. 页面级筛选（时间范围 / 语言 / 三分组基准 / 显示全部）**仍然是 URL 的唯一真相**
 *     （那些是"这块数据的口径声明"，见 ux-patterns §9），本 store 不接管它们。
 *
 *   代价（如实记录）：**分享链接不带全局归属**——收链接的人会用自己的 localStorage
 *   值。这是刻意的取舍：全局归属是"我在看哪个品牌/店铺"的会话上下文，不是单条链接
 *   的参数；要分享某个具体站点的广告树，用广告管理页自身的筛选条件表达。
 *
 * 与 `uiStore` 同一套约定：读不到 / 写不进去（隐私模式）都不抛错，本会话仍然生效。
 */

import { create } from "zustand";
import { ALL_BRANDS, ALL_SHOPS, resolveBrandDefault, resolveShopDefault } from "../lib/globalContext.js";
import { initialShopId, shopIdFromSearch, searchWithShopId } from "../lib/shopUrl.js";
import type { BrandOptionSource, ShopOptionSource } from "../lib/globalContext.js";

export const CONTEXT_BRAND_STORAGE_KEY = "ams.context.brandId";
export const CONTEXT_SHOP_STORAGE_KEY = "ams.context.shopId";

function readStored(key: string): string | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(key);
    return raw === null || raw === "" ? null : raw;
  } catch {
    return null;
  }
}

function persist(key: string, value: string): void {
  try {
    if (typeof window === "undefined") return;
    if (value === "") window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* 隐私模式下写不进去，忽略：本会话仍然生效 */
  }
}

export interface GlobalContextState {
  /** `""` = 全部品牌（多品牌默认）。 */
  readonly brandId: string;
  /** `""` = 全部店铺（用户指定的默认值）。 */
  readonly shopId: string;
  setBrandId: (brandId: string) => void;
  /**
   * ⭐ 设置站点：**先写 URL（真相）再镜像到 store**（单向）。同值 ⇒ 完全 no-op（幂等）。
   */
  setShopId: (shopId: string) => void;
  /**
   * ⭐ 把 URL 里的站点**镜像**进 store（前进/后退、外部链接）。
   * 只读 URL、只写 store ⇒ 与 `setShopId` 不构成循环（幂等）。
   */
  syncShopFromUrl: () => void;
  /**
   * 品牌列表到达后的**默认值收敛**（单品牌 → 默认选它；已存值已归档/不存在 → 回落）。
   * 幂等：收敛结果与当前值相同则不改 state（避免无意义的 re-render 与请求）。
   */
  reconcileBrands: (brands: readonly BrandOptionSource[]) => void;
  /**
   * 店铺列表到达后校正：当前 shopId 不属于所选品牌 → 回落「全部店铺」。
   * （品牌切换后必须有这一步，否则会停留在"上一个品牌的站点"上。）
   */
  reconcileShops: (shops: readonly ShopOptionSource[]) => void;
}

export const useContextStore = create<GlobalContextState>()((set, get) => ({
  brandId: readStored(CONTEXT_BRAND_STORAGE_KEY) ?? ALL_BRANDS,
  // ⭐ URL = 真相：URL 里有 `shopId` 就用它（覆盖 localStorage）；没有才回落 localStorage。
  shopId: initialShopId({
    search: typeof window === "undefined" ? "" : window.location.search,
    stored: readStored(CONTEXT_SHOP_STORAGE_KEY),
  }),

  setBrandId: (brandId) => {
    const next = brandId.trim();
    // 品牌一变，原站点几乎必然不属于新品牌：先回落「全部店铺」，
    // 由 reconcileShops 在店铺列表到达后再决定是否需要进一步校正。
    if (get().brandId === next && get().shopId === ALL_SHOPS) return;
    persist(CONTEXT_BRAND_STORAGE_KEY, next);
    persist(CONTEXT_SHOP_STORAGE_KEY, ALL_SHOPS);
    set({ brandId: next, shopId: ALL_SHOPS });
  },

  /**
   * ⭐ 设置站点：**先写 URL（真相），再镜像到 store**（单向；store 从不反向写 URL）。
   *
   *  · **同值 ⇒ 完全 no-op**（不导航、不 set）⇒ "重新选中同一个店"不会触发任何新请求
   *    （这正是防 effect 循环最直接的证据）；
   *  · `replaceState`（不是 push）⇒ 切站点不往浏览器历史里塞一堆记录；
   *  · localStorage 仅作**老链接回落缓存**（URL 丢失时还能记得上次选择），**不是真相源**。
   */
  setShopId: (shopId) => {
    const next = shopId.trim();
    if (get().shopId === next) {
      // 同值也要保证 URL 与 state 一致（例如手改 URL 后 state 落后）——但仍不导航不 set。
      const currentSearch = typeof window === "undefined" ? "" : window.location.search;
      if (shopIdFromSearch(currentSearch) === (next === "" ? null : next)) return;
    }
    if (typeof window !== "undefined") {
      const nextSearch = searchWithShopId(window.location.search, next);
      const url = `${window.location.pathname}${nextSearch}${window.location.hash}`;
      window.history.replaceState(window.history.state, "", url);
    }
    persist(CONTEXT_SHOP_STORAGE_KEY, next);
    set({ shopId: next });
  },

  /**
   * ⭐ 镜像：URL 变化（前进/后退 / 外部链接）⇒ 同步到 store。
   * **只读 URL、只写 store**（与 `setShopId` 的方向相反但**不构成循环**：
   * `setShopId` 写 URL 后不会再被这里"回写"成不同的值 —— 幂等）。
   */
  syncShopFromUrl: () => {
    if (typeof window === "undefined") return;
    const fromUrl = shopIdFromSearch(window.location.search) ?? "";
    if (get().shopId !== fromUrl) set({ shopId: fromUrl });
  },

  reconcileBrands: (brands) => {
    // 以**当前 state** 为准（它初始化自 localStorage、且 setBrandId 会写回）：
    // 这里再读一次存储既冗余，又会在 state 被程序化设置时得出不一致的结论。
    const current = get().brandId;
    const resolved = resolveBrandDefault(brands, current === ALL_BRANDS ? null : current);
    if (resolved !== current) {
      persist(CONTEXT_BRAND_STORAGE_KEY, resolved);
      set({ brandId: resolved });
    }
  },

  reconcileShops: (shops) => {
    const { brandId, shopId } = get();
    const resolved = resolveShopDefault(shops, brandId, shopId === "" ? null : shopId);
    if (resolved !== shopId) {
      persist(CONTEXT_SHOP_STORAGE_KEY, resolved);
      set({ shopId: resolved });
    }
  },
}));

/** 供 API 调用方拼 query 用：`""` → 不传（= 全部）。 */
export function optionalShopId(shopId: string): string | undefined {
  return shopId === ALL_SHOPS ? undefined : shopId;
}

/** 供 API 调用方拼 query 用：`""` → 不传（= 全部品牌）。 */
export function optionalBrandId(brandId: string): string | undefined {
  return brandId === ALL_BRANDS ? undefined : brandId;
}
